import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import { C, S } from '../../utils/styles';

export default function FederalConstantsEditor() {
  const { config, updatePayrollConstants, updateStandardDeduction } = useConfig();
  const addToast = useToast();
  const [draftConstants, setDraftConstants] = useState({ ...config.payrollConstants });
  const [draftDeductions, setDraftDeductions] = useState({ ...config.standardDeduction });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setDraftConstants({ ...config.payrollConstants });
    setDraftDeductions({ ...config.standardDeduction });
    setHasChanges(false);
  }, [config.payrollConstants, config.standardDeduction]);

  const updateConst = (key, value) => {
    setDraftConstants(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
    setHasChanges(true);
  };

  const updateDed = (key, value) => {
    setDraftDeductions(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
    setHasChanges(true);
  };

  const save = () => {
    updatePayrollConstants(draftConstants);
    updateStandardDeduction(draftDeductions);
    setHasChanges(false);
    addToast('Federal constants saved');
  };

  const cancel = () => {
    setDraftConstants({ ...config.payrollConstants });
    setDraftDeductions({ ...config.standardDeduction });
    setHasChanges(false);
  };

  const constantFields = [
    { key: 'ssRate', label: 'SS Rate', displayMultiplier: 100, suffix: '%', step: '0.1', desc: 'Social Security tax rate' },
    { key: 'ssWageBase', label: 'SS Wage Base', prefix: '$', step: '100', desc: 'Social Security wage base cap' },
    { key: 'medicareRate', label: 'Medicare Rate', displayMultiplier: 100, suffix: '%', step: '0.01', desc: 'Medicare tax rate' },
    { key: 'addlMedicareRate', label: 'Addl. Medicare Rate', displayMultiplier: 100, suffix: '%', step: '0.1', desc: 'Additional Medicare tax rate' },
    { key: 'employerFicaRate', label: 'Employer FICA Rate', displayMultiplier: 100, suffix: '%', step: '0.01', desc: 'Employer-side FICA rate' },
    { key: 'futaRate', label: 'FUTA Rate', displayMultiplier: 100, suffix: '%', step: '0.1', desc: 'Federal Unemployment Tax Act rate' },
    { key: 'futaWageBase', label: 'FUTA Wage Base', prefix: '$', step: '100', desc: 'FUTA taxable wage base per employee' },
    { key: 'minWageHourly', label: 'Minimum Wage (hourly)', prefix: '$', step: '0.25', desc: 'Federal minimum wage for viability check' },
  ];

  const medicareThresholdFields = [
    { key: 'single', label: 'Single (S)' },
    { key: 'married', label: 'Married Filing Jointly (MFJ)' },
    { key: 'mfs', label: 'Married Filing Separately (MFS)' },
    { key: 'hoh', label: 'Head of Household (HoH)' },
    { key: 'qss', label: 'Qualifying Surviving Spouse (QSS)' },
  ];

  const deductionFields = [
    { key: 'single', label: 'Single (S)' },
    { key: 'married', label: 'Married Filing Jointly (MFJ)' },
    { key: 'mfs', label: 'Married Filing Separately (MFS)' },
    { key: 'hoh', label: 'Head of Household (HoH)' },
    { key: 'qss', label: 'Qualifying Surviving Spouse (QSS)' },
  ];

  const updateThreshold = (key, value) => {
    setDraftConstants(prev => ({
      ...prev,
      addlMedicareThresholds: {
        ...prev.addlMedicareThresholds,
        [key]: parseFloat(value) || 0
      }
    }));
    setHasChanges(true);
  };

  return (
    <div data-testid="federal-constants-editor" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ ...S.heading, margin: 0 }}>Payroll Tax Constants</h3>
            {hasChanges && <span style={{ fontSize: 9, color: C.orange, fontWeight: 700, background: C.orangeLight, padding: '2px 8px', borderRadius: 4 }}>Unsaved Changes</span>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {constantFields.map(f => (
            <div key={f.key} style={{ padding: '10px 14px', background: C.bg, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
              <label style={{ ...S.label, marginBottom: 6 }}>{f.label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {f.prefix && <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>{f.prefix}</span>}
                <input
                  type="number"
                  value={f.displayMultiplier ? (draftConstants[f.key] * f.displayMultiplier).toFixed(2) : draftConstants[f.key]}
                  onChange={(e) => updateConst(f.key, f.displayMultiplier ? parseFloat(e.target.value) / f.displayMultiplier : e.target.value)}
                  step={f.step}
                  style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
                />
                {f.suffix && <span style={{ ...S.mono, fontSize: 12, color: C.textLight }}>{f.suffix}</span>}
              </div>
              <div style={{ fontSize: 10, color: C.textLight, marginTop: 4 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h3 style={S.heading}>Additional Medicare Thresholds</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          Filing-status-dependent income thresholds for the {((draftConstants.addlMedicareRate || 0) * 100).toFixed(2)}% Additional Medicare Tax.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          {medicareThresholdFields.map(f => (
            <div key={f.key} style={{ padding: '10px 14px', background: C.bg, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
              <label style={{ ...S.label, marginBottom: 6, fontSize: 10 }}>{f.label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
                <input
                  type="number"
                  value={(draftConstants.addlMedicareThresholds && draftConstants.addlMedicareThresholds[f.key]) || 0}
                  onChange={(e) => updateThreshold(f.key, e.target.value)}
                  step="1000"
                  style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h3 style={S.heading}>Standard Deductions</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          {deductionFields.map(f => (
            <div key={f.key} style={{ padding: '10px 14px', background: C.bg, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
              <label style={{ ...S.label, marginBottom: 6, fontSize: 10 }}>{f.label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
                <input
                  type="number"
                  value={draftDeductions[f.key] || 0}
                  onChange={(e) => updateDed(f.key, e.target.value)}
                  step="500"
                  style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasChanges && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button 
            onClick={cancel} 
            style={S.buttonSecondary}
          >
            Cancel
          </button>
          <button 
            data-testid="save-constants-btn" 
            onClick={save} 
            style={S.buttonPrimary}
          >
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
}
